/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import SvgExternal from '@digital-hig/icon/lib/build/icons/navigation/external';
import { Button, SvgIcon, Tab } from '@mui/material';
import Tabs from '@digital-hig/tabs';
import { useTranslation } from 'react-i18next';
import TabsRootElement from './style';
import { ProfileContextConsumer } from '../../../common/context/profile/context';
import { isItemAvailableInside } from '../../../common/accordionList';

const ProfileTabs = ({ scrollRef, partnerDetails, profileDetails }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const contactBtn = useRef();
  const { t } = useTranslation();
  const {
    contact_link: rawContactLink,
    accreditations = {},
    affiliations = [],
    services = [],
    specializations = [],
    solutions = {},
  } = partnerDetails || {};
  const { successStories = [] } = profileDetails || {};

  let contactLink = '';
  if (rawContactLink) {
    const trimmedLink = rawContactLink.trim();
    if (/^https?:\/\//i.test(trimmedLink)) {
      contactLink = trimmedLink;
    } else if (/^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(trimmedLink) && /^mailto:\/\//i.test(trimmedLink)) {
      contactLink = trimmedLink;
    } else if (/^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(trimmedLink)) {
      contactLink = `mailto:${trimmedLink}`;
    } else if (/^\+?\d[\d\s-]{7,}$/i.test(trimmedLink) && /^tel:\/\//i.test(trimmedLink)) {
      contactLink = trimmedLink;
    } else if (/^\+?\d[\d\s-]{7,}$/i.test(trimmedLink)) {
      contactLink = `tel:${trimmedLink.replace(/\s+/g, '')}`;
    } else {
      contactLink = `https://${trimmedLink.replace(/^\/\//, '')}`;
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const tabRects = scrollRef.current.map((tab) => tab.getBoundingClientRect());
      if (contactBtn.current?.style) {
        contactBtn.current.style.display = window.scrollY > 365 ? 'inline-flex' : 'none';
      }
      const newActiveTabIndex = tabRects.findIndex((tabRect) => (tabRect.top < 150 && tabRect.top >= 0) || (tabRect.top < 0 && Math.abs(tabRect.top) + 150 <= Math.abs(tabRect.height)));
      if (newActiveTabIndex !== -1 && newActiveTabIndex !== tabIndex) {
        setTabIndex(newActiveTabIndex);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef, tabIndex]);

  const scrollToItems = (id) => {
    setTabIndex(id);
    const targetPosition = scrollRef.current[id].getBoundingClientRect().top
            + window.scrollY
            - 100;
    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
  };

  return (
    <TabsRootElement>
      <Box className="page-container">
        <Tabs
          value={tabIndex}
          onChange=""
          styleVariant="quiet"
          variant="scrollable"
          scrollButtons={false}
          aria-label="scrollable prevent tabs example"
          className="profile-tab"
        >
          <Tab
            id="tab-0--quiet"
            data-wat-val="overview"
            data-wat-loc="secondary nav"
            label={`${t('Overview')}`}
            onClick={() => scrollToItems(0)}
          />
          {affiliations.length > 0 && (
            <Tab
              id="tab-1--quiet"
              data-wat-val="affiliations"
              data-wat-loc="secondary nav"
              label={`${t('Affiliations')}`}
              onClick={() => scrollToItems(1)}
            />
          )}
          {services.length > 0 && (
            <Tab
              id="tab-2--quiet"
              data-wat-val="services"
              data-wat-loc="secondary nav"
              label={`${t('Services')}`}
              onClick={() => scrollToItems(2)}
            />
          )}
          {specializations.length > 0 && (
            <Tab
              id="tab-3--quiet"
              data-wat-val="specializations"
              data-wat-loc="secondary nav"
              label={`${t('Specializations')}`}
              onClick={() => scrollToItems(3)}
            />
          )}
          {Object.keys(solutions).map((item) => isItemAvailableInside(item, solutions)).includes(true) && (
            <Tab
              id="tab-4--quiet"
              data-wat-val="solutions"
              data-wat-loc="secondary nav"
              label={`${t('Solutions')}`}
              onClick={() => scrollToItems(4)}
            />
          )}
          {Object.keys(accreditations).map((item) => isItemAvailableInside(item, accreditations)).includes(true) && (
            <Tab
              id="tab-5--quiet"
              data-wat-val="accreditations"
              data-wat-loc="secondary nav"
              label={`${t('Accreditations')}`}
              onClick={() => scrollToItems(5)}
            />
          )}
          {successStories.length > 0 && (
            <Tab
              id="tab-6--quiet"
              data-wat-val="success stories"
              data-wat-loc="secondary nav"
              label={`${t('Success stories')}`}
              onClick={() => scrollToItems(6)}
            />
          )}
          {contactLink && (
            <Button
              ref={contactBtn}
              startIcon={<SvgIcon fontSize="medium"><SvgExternal /></SvgIcon>}
              variant="contained"
              href={contactLink}
              data-wat-loc="hero contact"
              data-wat-val="contact us"
              target="_blank"
              className="tab-contact-bt tab-content-hide"
            >
              {t('Contact Partner')}
            </Button>
          )}
        </Tabs>
      </Box>
    </TabsRootElement>
  );
};

ProfileTabs.propTypes = {
  scrollRef: PropTypes.shape({ current: [] }),
  partnerDetails: PropTypes.shape({}),
  profileDetails: PropTypes.shape({}),
};
ProfileTabs.defaultProps = {
  scrollRef: {},
  partnerDetails: {},
  profileDetails: {},
};

export default ProfileContextConsumer(ProfileTabs);
